import { useState, useEffect } from 'react';
import FeatherIcon from 'feather-icons-react';
import { getIncidentSite } from './incidentSite';

const getPersonName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim() || null;

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
  } catch {
    return null;
  }
};

const MetaRow = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div style={styles.metaRow}>
      <span style={styles.metaIcon}><FeatherIcon icon={icon} size={13} /></span>
      <span style={styles.metaLabel}>{label}</span>
      <span style={styles.metaValue}>{value}</span>
    </div>
  );
};

const deriveReportMeta = (report) => {
  const persons = report?.notification?.personsInvolved || [];
  const personNames = persons.map((p) => getPersonName(p)).filter(Boolean);
  const site = getIncidentSite(report);

  return {
    occurrenceDate: formatDate(report?.investigation?.occurrenceDateTime) ?? 'N/A',
    siteAddress: site === '—' ? 'N/A' : site,
    personDisplay:
      personNames.length === 0 ? 'N/A'
        : personNames.length === 1 ? personNames[0]
          : `${personNames[0]} +${personNames.length - 1} more`,
    incidentType: null,
    reportedBy:
      report?.completedBy?.role
        ? report.completedBy.role.charAt(0).toUpperCase() + report.completedBy.role.slice(1)
        : report?.notification?.managerReporting ?? 'N/A',
  };
};

const deriveInvitationMeta = (invitation) => {
  const people = invitation?.prefilledPerson || [];
  const recipientNames = people
    .map((p) => [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || p.email?.split('@')[0])
    .filter(Boolean);

  return {
    referenceId: invitation?.referenceId ?? null,
    sentDate: formatDate(invitation?.createdAt) ?? 'N/A',
    building: invitation?.property?.buildingName || invitation?.property?.property || 'N/A',
    recipients:
      recipientNames.length === 0 ? 'N/A'
        : recipientNames.length === 1 ? recipientNames[0]
          : `${recipientNames[0]} +${recipientNames.length - 1} more`,
    expiresDate: formatDate(invitation?.expiresAt) ?? null,
  };
};

const DeleteConfirmationModal = ({ open, onClose, onDelete, report, mode = 'reports' }) => {
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) requestAnimationFrame(() => setVisible(true));
    else setVisible(false);
  }, [open]);

  if (!open) return null;

  const isReport = mode === 'reports';
  const meta = isReport ? deriveReportMeta(report) : deriveInvitationMeta(report);

  const handleConfirm = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await onDelete?.();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  return (
    <>
      <div
        style={{ ...styles.backdrop, opacity: visible ? 1 : 0 }}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        style={{
          ...styles.dialogWrapper,
          opacity: visible ? 1 : 0,
          transform: visible
            ? 'translate(-50%, -50%) scale(1)'
            : 'translate(-50%, -48%) scale(0.97)',
          transition: 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <button style={styles.closeBtn} onClick={handleClose} disabled={loading} aria-label="Close">
          <FeatherIcon icon="x" size={16} />
        </button>

        <div style={styles.iconWrap}>
          <div style={styles.iconRing}>
            <FeatherIcon icon="trash-2" size={22} color="#dc2626" />
          </div>
        </div>

        <div style={styles.body}>
          <h2 id="delete-modal-title" style={styles.title}>
            Delete {isReport ? 'Incident Report' : 'Invitation'}
          </h2>
          <p style={styles.subtitle}>
            You're about to permanently delete this {isReport ? 'report' : 'invitation'}. This action{' '}
            <strong style={{ color: '#1e293b' }}>cannot be undone</strong>
            {isReport
              ? ' and will remove all associated data, attachments, and follow-up actions.'
              : ' and the recipient will no longer be able to use this invitation link.'}
          </p>

          <div style={styles.card}>
            <div style={styles.cardLabel}>{isReport ? 'Report details' : 'Invitation details'}</div>

            {isReport ? (
              <>
                <MetaRow icon="map-pin" label="Site" value={meta.siteAddress} />
                <MetaRow icon="calendar" label="Occurred" value={meta.occurrenceDate} />
                <MetaRow icon="user" label="Involved" value={meta.personDisplay} />
                {meta.incidentType && (
                  <MetaRow icon="tag" label="Type" value={meta.incidentType} />
                )}
                <MetaRow icon="user-check" label="Reported by" value={meta.reportedBy} />
              </>
            ) : (
              <>
                {meta.referenceId && (
                  <MetaRow icon="hash" label="Ref #" value={meta.referenceId} />
                )}
                <MetaRow icon="calendar" label="Sent" value={meta.sentDate} />
                <MetaRow icon="home" label="Building" value={meta.building} />
                <MetaRow icon="users" label="Recipients" value={meta.recipients} />
                {meta.expiresDate && (
                  <MetaRow icon="clock" label="Expires" value={meta.expiresDate} />
                )}
              </>
            )}
          </div>

          <div style={styles.warningBanner}>
            <FeatherIcon icon="alert-circle" size={14} color="#b45309" />
            <span style={{ fontSize: '12.5px', color: '#92400e', lineHeight: 1.4 }}>
              {isReport
                ? 'All linked invitation data, attachments, and history will also be permanently removed.'
                : 'Any recipient who has not yet submitted their report will lose access to this link.'}
            </span>
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button
            style={{ ...styles.deleteBtn, ...(loading ? styles.deleteBtnLoading : {}) }}
            onClick={handleConfirm}
            disabled={loading}
          >
            <span style={styles.btnInner}>
              {loading ? (
                <>
                  <span style={styles.spinner} />
                  Deleting…
                </>
              ) : (
                <>
                  <FeatherIcon icon="trash-2" size={15} />
                  Delete {isReport ? 'Report' : 'Invitation'}
                </>
              )}
            </span>
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 1050,
    transition: 'opacity 0.2s ease',
  },
  dialogWrapper: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    zIndex: 1051,
    width: 'calc(100% - 32px)',
    maxWidth: '440px',
    background: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  closeBtn: {
    position: 'absolute',
    top: '14px',
    right: '14px',
    width: '30px',
    height: '30px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '32px',
    paddingBottom: '4px',
  },
  iconRing: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #fff1f1, #ffe4e4)',
    border: '2px solid #fecaca',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { padding: '16px 28px 20px' },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#0f172a',
    textAlign: 'center',
    margin: '0 0 8px',
  },
  subtitle: {
    fontSize: '13.5px',
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 1.6,
    margin: '0 0 18px',
  },
  card: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '12px',
  },
  cardLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    marginBottom: '10px',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '7px',
    marginBottom: '7px',
  },
  metaIcon: { color: '#94a3b8', flexShrink: 0, position: 'relative', top: '1px' },
  metaLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    fontWeight: 500,
    minWidth: '72px',
    flexShrink: 0,
  },
  metaValue: { fontSize: '12.5px', color: '#334155', fontWeight: 500, wordBreak: 'break-word' },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '10px',
    padding: '10px 12px',
  },
  footer: {
    display: 'flex',
    gap: '10px',
    padding: '16px 28px 24px',
    borderTop: '1px solid #f1f5f9',
  },
  cancelBtn: {
    flex: 1,
    height: '42px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    background: '#ffffff',
    color: '#475569',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  deleteBtn: {
    flex: 1,
    height: '42px',
    border: 'none',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(220,38,38,0.3)',
  },
  deleteBtnLoading: { opacity: 0.75, cursor: 'not-allowed', boxShadow: 'none' },
  btnInner: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' },
  spinner: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
};

export { DeleteConfirmationModal };